import { string, func, bool } from 'prop-types';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Suspense } from 'react';
import usePreviouslyAssigned from '../../data/queryHooks/shared/physicalTherapist/usePreviouslyAssigned';
import PreviouslyAssignedSkeleton from '../../components/shared/PreviouslyAssignedSkeleton/PreviouslyAssignedSkeleton';
import ActionPlanCard from '../../components/shared/components/ActionPlanCard/ActionPlanCard';
import emptyCards from '../../components/shared/assets/emptyCards.webp'

const CoachingPreviouslyAssignedContent = ({
	apiEnv,
	ssId,
	apiToken,
	memberId,
	navigateToResourceLibrary,
	isAdmin,
}) => {
	const memberProgramDetailHistory = usePreviouslyAssigned(
		apiEnv,
		ssId,
		'coachingProgram',
		isAdmin,
		apiToken,
		memberId,
	);

	// If there are no previously assigned items, display a special view.
	if (!memberProgramDetailHistory || memberProgramDetailHistory.length === 0) {
		return (
			<Box textAlign="center" mt={16}>
				<Stack justifyContent="center" direction="row" mb={2}>
					<img width="200px" mx="auto" src={emptyCards} alt="Cards spread out showing no exercsies assigned yet." />
				</Stack>
				<Typography variant="subtitle1" component="h2">
					No previously assigned items yet.
				</Typography>
				<Typography variant="body1" component="h3" color="text.secondary">
					Items that your care team member previously assigned will be shown
					here.
				</Typography>
			</Box>
		);
	}

	return (
		<Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={4} m="auto">
			{memberProgramDetailHistory.map(memberActionItem => {
				const { id, resource } = memberActionItem;
				return (
					<Box
						gridColumn={{ xs: 'span 12', sm: 'span 6', lg: 'span 4' }}
						key={id}
					>
						<ActionPlanCard
							id={id}
							resource={resource}
							apiEnv={apiEnv}
							ssId={ssId}
							handleCardClick={() => {
								navigateToResourceLibrary(resource?.id); // consumer defined callback function
							}}
						/>
					</Box>
				);
			})}
		</Box>
	);
};

CoachingPreviouslyAssignedContent.propTypes = {
	/*
	 * The environment the component is being hosted on such as "prod" or "dev"
	 */
	apiEnv: string.isRequired,
	/*
	 * Session ID to be added to the header when calling the API.
	 */
	ssId: string,
	/*
	 * API token to be added to the header when calling the API.
	 */
	apiToken: string,
	/*
	 * Callback function for when a user clicks on a card on the previously assigned page.
	 * callback function takes one argument, resouceId
	 */
	navigateToResourceLibrary: func.isRequired,
	/*
	 * Member ID to be added to the header when calling the API on Admin Sites.
	 */
	memberId: string,
	/*
	 * Flag to tell our component how to route the requests.
	 */
	isAdmin: bool,
};

CoachingPreviouslyAssignedContent.defaultProps = {
	apiToken: '',
	ssId: '',
	memberId: '',
	isAdmin: false,
};

const CoachingPreviouslyAssignedPage = ({
	apiEnv,
	ssId,
	apiToken,
	navigateToResourceLibrary,
	memberId,
	isAdmin,
}) => (
	<Box component="section">
		<Button
			onClick={() => window.history.back()}
			startIcon={<ChevronLeftIcon />}
		>
			Back
		</Button>
		<Typography textAlign="center" variant="h5" component="h1" pt={5} pb={10}>
			Previously assigned exercises
		</Typography>
		<Suspense fallback={<PreviouslyAssignedSkeleton />}>
			<CoachingPreviouslyAssignedContent
				apiEnv={apiEnv}
				ssId={ssId}
				apiToken={apiToken}
				navigateToResourceLibrary={navigateToResourceLibrary}
				memberId={memberId}
				isAdmin={isAdmin}
			/>
		</Suspense>
	</Box>
);

CoachingPreviouslyAssignedPage.propTypes = {
	/*
	 * The environment the component is being hosted on such as "prod" or "dev".
	 */
	apiEnv: string.isRequired,
	/*
	 * Session ID to be added to the header when calling the API.
	 */
	ssId: string,
	/*
	 * API token to be added to the header when calling the API.
	 */
	apiToken: string,
	/*
	 * Member ID to be added to the header when calling the API on Admin Sites.
	 */
	memberId: string,

	/*
	 * Callback function for when a user clicks on a card on the previously assigned page.
	 * callback function takes one argument, resouceId
	 */
	navigateToResourceLibrary: func.isRequired,
	/*
	 * Flag to tell our component how to route the requests.
	 */
	isAdmin: bool,
};

CoachingPreviouslyAssignedPage.defaultProps = {
	ssId: '',
	apiToken: '',
	memberId: '',
	isAdmin: false,
};

export default CoachingPreviouslyAssignedPage;
